import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { buildLabel } from "./helpers/input-labels";
import { useTrans } from "../../hooks";
import moment from "moment";

import Icon from "../Icon";

/**
 *  DateTime Fields
 *  Takes in UTC time, converts to local for display and passes UTC back to onChange
 *
 */
const DateTimeField = ({
  className,
  labelClassName,
  label,
  value,
  name,
  min,
  max,
  onChange,
  disabled,
  required,
  timeIncrements
}) => {
  const { trans } = useTrans();
  const optionalAttributes = {};
  min && (optionalAttributes.min = min);
  max && (optionalAttributes.max = max);
  const [selectedDate, setSelectedDate] = useState(
    moment(value)
      .locale("en")
      .format("YYYY-MM-DD")
  );
  const [selectedTime, setSelectedTime] = useState(generateDefaultTimeString());
  const [availableTimes, setAvailableTimes] = useState([]);

  const handleTimeChange = newTime => {
    setSelectedTime(newTime);
    let splitTimes = newTime.split(":");
    let comboDate = moment(selectedDate)
      .locale("en")
      .set("hours", splitTimes[0])
      .set("minutes", splitTimes[1]);
    onChange(moment(comboDate).toISOString());
  };

  function generateDefaultTimeString() {
    return moment(value).isAfter()
      ? moment(value)
          .locale("en")
          .format("HH:mm:00")
      : "";
  }

  const generateAvailableTimes = useCallback(
    () => {
      let availableTimes = [];

      let endTime = moment().endOf("day");
      let startTime = moment().startOf("day");
      let selectValue = moment()
        .locale("en")
        .startOf("day");
      // If publish date is desired today don't let it be set in the past
      if (moment(selectedDate).isSame(startTime, "day")) {
        startTime = moment().add(
          timeIncrements +
            (timeIncrements - (moment().minute() % timeIncrements)),
          "m"
        );
        selectValue = moment()
          .locale("en")
          .add(
            timeIncrements +
              (timeIncrements - (moment().minute() % timeIncrements)),
            "m"
          );
      }
      while (startTime < endTime) {
        availableTimes.push({
          displayValue: startTime.format("hh:mm A"),
          value: selectValue.format("HH:mm:00")
        });
        startTime.add(15, "m");
        selectValue.add(15, "m");
      }

      return availableTimes;
    },
    [selectedDate, timeIncrements]
  );

  useEffect(
    () => {
      setAvailableTimes(generateAvailableTimes());
    },
    [setAvailableTimes, generateAvailableTimes]
  );

  return (
    <div className={classNames("date-time-field", className)}>
      {buildLabel({
        label,
        labelClassName
      })}
      <div className="field-row">
        <input
          className="date-form-field text-body"
          type="date"
          name={name}
          {...optionalAttributes}
          disabled={disabled}
          required={required}
          onChange={e => {
            setSelectedDate(e.target.value);
            setSelectedTime("");
          }}
          value={selectedDate}
        />
        <div className="select-container">
          <select
            className="time-form-field text-body"
            onChange={e => {
              handleTimeChange(e.target.value);
            }}
            value={selectedTime}
          >
            <option value="">{trans("Time")}</option>
            {availableTimes.map(time => {
              return (
                <option key={`option-${time.value}`} value={time.value}>
                  {time.displayValue}
                </option>
              );
            })}
          </select>
          <Icon name="history" className="time-icon vcenter" />
        </div>
      </div>
    </div>
  );
};

DateTimeField.defaultProps = {
  disabled: false,
  required: false,
  onChange: () => {},
  timeIncrements: 15
};

DateTimeField.propTypes = {
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  name: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  timeIncrements: PropTypes.number
};

export default DateTimeField;
